import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { CheckoutContext, CheckoutContextProps } from '../../../../context/CheckoutContext';
import { useAuth } from '../../../../context/AuthContext';
import useDfcAuthEntry from '../../../../hooks/useDfcAuthEntry';
import useDfcSurface from '../../../../hooks/useDfcSurface';
import useFitnessVillageCopy from '../../../../hooks/useFitnessVillageCopy';
import { isValidUser } from '../../../../Utils/validate';
import useDfcVillageNavItems, {
  isExternalNavTarget,
} from '../../header/hooks/useDfcVillageNavItems';
import { SidebarProps } from '../../Sidebar/Type';
import { Eyebrow } from '../style';
import {
  AuthActions,
  CloseButton,
  Footnote,
  MenuButton,
  MenuPanel,
  MenuTop,
  NavList,
  NavRow,
} from './style';

/**
 * The Fitness Village theme's mobile menu — what the header's burger opens in place of the
 * shared `Sidebar` (see `Layout`). The same pages as the desktop nav, then the account
 * actions: Sign in for a visitor (through the shared auth overlay; its Create account tab is
 * one tap away), My profile / Log out for an account holder — the two things the shared
 * Sidebar offered them.
 *
 * MUI's Drawer supplies the modal behaviour: focus moves in on open, is trapped while open,
 * and returns to the burger on close; Escape and the scrim both close it.
 */
const DfcVillageMenuDrawer: React.FC<SidebarProps> = ({ isOpen, toggleDrawer }) => {
  const surface = useDfcSurface();
  const copy = useFitnessVillageCopy().header;
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const navItems = useDfcVillageNavItems(company);
  const { handleSignIn } = useDfcAuthEntry();

  const isSignedIn = isValidUser(user);
  const close = useCallback(() => toggleDrawer(false), [toggleDrawer]);

  const goTo = useCallback(
    (to: string) => {
      close();
      if (isExternalNavTarget(to)) {
        window.open(to, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(to);
    },
    [close, navigate]
  );

  const profilePath = `/My-profile?companyId=${company?.id}`;
  // A company's own sidebarContent may already link the profile; don't list it twice.
  const showProfileRow = isSignedIn && !navItems.some(item => item.to === profilePath);

  const thenClose = (action: () => void) => () => {
    close();
    action();
  };

  const handleLogout = async () => {
    close();
    await logout();
    navigate(`/?companyId=${company?.id}`);
  };

  return (
    <MenuPanel
      surface={surface}
      anchor="right"
      open={isOpen}
      onClose={close}
      PaperProps={{ 'aria-label': copy.menu }}
      data-testid="dfc-menu-drawer"
    >
      <MenuTop>
        <Eyebrow tone={surface.ink2}>{copy.menu}</Eyebrow>
        <CloseButton surface={surface} onClick={close} aria-label={copy.closeMenu}>
          <CloseIcon />
        </CloseButton>
      </MenuTop>

      <NavList aria-label={copy.mainNav}>
        {navItems.map(item => (
          <NavRow
            key={item.to}
            surface={surface}
            active={item.active}
            aria-current={item.active ? 'page' : undefined}
            onClick={() => goTo(item.to)}
          >
            {item.name}
            <ArrowForwardIcon />
          </NavRow>
        ))}
        {showProfileRow && (
          <NavRow surface={surface} active={false} onClick={() => goTo(profilePath)}>
            {copy.myProfile}
            <ArrowForwardIcon />
          </NavRow>
        )}
      </NavList>

      <AuthActions>
        {isSignedIn ? (
          <MenuButton surface={surface} variant="outline" onClick={handleLogout}>
            {copy.logOut}
          </MenuButton>
        ) : (
          <MenuButton surface={surface} variant="solid" onClick={thenClose(handleSignIn)}>
            {copy.signIn}
          </MenuButton>
        )}
      </AuthActions>

      <Footnote surface={surface}>{copy.menuFootnote}</Footnote>
    </MenuPanel>
  );
};

export default DfcVillageMenuDrawer;
